/*
 * JEFE cart controller v5 — gifts + tier/cadence subscription repricing.
 * - ProductForm awaits add(): main variant and missing gift variants go in one POST.
 * - CartItems awaits change(): reconcile gifts BEFORE rendering cart sections.
 * - Only actual subscription lines qualify; ordinary purchased products are untouched.
 * - Shopify remains the source of product prices, inventory and discounts.
 * This is storefront orchestration, not server-side promotion/anti-abuse enforcement.
 */
(() => {
  'use strict';
  if (window.JefeGiftCart) return;
  const MARKER = '_jefe_gift';
  let tail = Promise.resolve();
  let pending = 0;
  const replaying = new WeakSet();
  const object = (value) => value && typeof value === 'object' ? value : {};
  const positiveInt = (value) => Number.isSafeInteger(Number(value)) && Number(value) > 0 ? Number(value) : null;
  const isGift = (item) => String(object(item.properties)[MARKER]) === '1';
  const planId = (item) => String(item.selling_plan_allocation?.selling_plan?.id || item.selling_plan || '');
  const variantId = (item) => String(item.variant_id || item.id || '');
  const root = () => window.Shopify?.routes?.root || '/';
  const endpoint = (action) => {
    const name = action === 'get' ? 'cart_url' : `cart_${action}_url`;
    const fallback = `${root()}cart${action === 'get' ? '' : `/${action}`}.js`;
    const url = new URL(window.routes?.[name] || fallback, location.origin);
    if (!/\.js$/.test(url.pathname)) url.pathname = url.pathname.replace(/\.(json|js)$/, '') + '.js';
    return url.pathname + url.search;
  };
  const sectionIds = (value) => [...new Set((Array.isArray(value) ? value : String(value || '').split(',')).filter(Boolean))].slice(0, 5);
  const visibleSectionIds = () => sectionIds([
    document.querySelector('cart-drawer') ? 'cart-drawer' : '',
    'cart-icon-bubble',
    document.getElementById('main-cart-items')?.dataset.id,
    document.getElementById('main-cart-footer')?.dataset.id,
  ]);

  function enqueue(task) {
    pending += 1;
    document.documentElement.classList.add('jefe-gift-cart-busy');
    const run = tail.then(task, task);
    tail = run.catch(() => undefined);
    return run.finally(() => {
      pending -= 1;
      if (!pending) document.documentElement.classList.remove('jefe-gift-cart-busy');
    });
  }

  async function jsonRequest(url, options = {}) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(url, {
        credentials: 'same-origin', cache: 'no-store', ...options,
        signal: controller.signal,
        headers: { Accept: 'application/json', ...(options.headers || {}) },
      });
      const data = await response.json();
      if (!response.ok || data?.errors || Number(data?.status) >= 400) {
        const text = data?.description || data?.message || data?.errors;
        const error = new Error(typeof text === 'string' ? text : 'Shopify could not update the selected products.');
        error.payload = data;
        throw error;
      }
      return data;
    } catch (error) {
      if (error.name === 'AbortError') throw new Error('The cart update timed out. Check your cart before trying again.');
      throw error;
    } finally { clearTimeout(timer); }
  }
  const getCart = () => jsonRequest(endpoint('get'));
  const post = (action, body) => jsonRequest(endpoint(action), {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  });
  function propertiesFromForm(formData) {
    const properties = Object.create(null);
    for (const [name, value] of formData.entries()) {
      const match = name.match(/^properties\[(.+)\]$/);
      if (!match) continue;
      if (typeof value !== 'string') {
        if (value.size) throw new Error('This offer does not support file-upload properties.');
        continue;
      }
      if (value !== '') properties[match[1]] = value;
    }
    return properties;
  }
  const sortedProps = (properties) => JSON.stringify(Object.entries(object(properties)).filter(([, v]) => v !== null && v !== '').map(([k,v]) => [k,String(v)]).sort(([a],[b]) => a.localeCompare(b)));
  const sameMainLine = (a,b) => !isGift(a) && variantId(a) === variantId(b) && planId(a) === planId(b) && sortedProps(a.properties) === sortedProps(b.properties);

  function rules(item) {
    if (isGift(item)) return [];
    const properties = object(item.properties);
    const scope = String(properties._jefe_gift_scope || item.product_id || variantId(item));
    const result = [];
    for (const [key,value] of Object.entries(properties)) {
      const match = key.match(/^_jefe_gift_(\d+)_variant$/);
      if (!match) continue;
      const id = positiveInt(value);
      const minimum = Math.max(1, positiveInt(properties[`_jefe_gift_${match[1]}_min`]) || 3);
      if (id && String(id) !== variantId(item)) {
        result.push({ id, minimum, slot: match[1], scope, key: `${scope}|${match[1]}|${id}` });
      }
    }
    return result;
  }
  function expectedGifts(cart) {
    // Bundle quantities can be split across multiple flavor variants. Aggregate all
    // qualifying parent lines by gift scope before applying the 2-bag / 4-bag threshold.
    const totals = new Map();
    for (const item of cart.items || []) {
      if (isGift(item)) continue;
      for (const rule of rules(item)) {
        const current = totals.get(rule.key) || { ...rule, quantity: 0 };
        current.quantity += Math.max(0, Number(item.quantity) || 0);
        totals.set(rule.key, current);
      }
    }
    const expected = new Map();
    totals.forEach((rule, key) => {
      if (rule.quantity >= rule.minimum) expected.set(key, rule);
    });
    return expected;
  }
  const managedKey = (item) => `${object(item.properties)._jefe_gift_scope || ''}|${object(item.properties)._jefe_gift_slot ?? ''}|${variantId(item)}`;
  function managedGifts(cart) {
    const managed = new Map();
    for (const item of cart.items || []) {
      if (!isGift(item)) continue;
      const key = managedKey(item);
      const list = managed.get(key) || [];
      list.push(item); managed.set(key,list);
    }
    return managed;
  }
  const giftItem = (rule) => ({ id: rule.id, quantity: 1, properties: {
    [MARKER]: '1', _jefe_gift_scope: rule.scope, _jefe_gift_slot: rule.slot,
  } });

  const bundleScope = (item) => String(object(item.properties)._jefe_bundle_scope || object(item.properties)._jefe_gift_scope || '');
  const tierForQuantity = (quantity) => quantity <= 1 ? 1 : quantity === 2 ? 2 : quantity === 3 ? 3 : 4;
  function tierRegistry() {
    if (window.JefeTierPlanRegistry && typeof window.JefeTierPlanRegistry === 'object') return window.JefeTierPlanRegistry;
    try {
      const stored = JSON.parse(sessionStorage.getItem('jefe:tier-plan-registry') || '{}');
      if (stored && typeof stored === 'object') window.JefeTierPlanRegistry = stored;
    } catch (_) {}
    return window.JefeTierPlanRegistry || {};
  }
  const requestedFrequencyDays = (item) => {
    const days = positiveInt(object(item.properties)._jefe_frequency_days);
    return days === 30 || days === 60 ? days : null;
  };
  const tierPlanFromItem = (item, tier, days) => {
    const properties = object(item.properties);
    const exact = positiveInt(properties[`_jefe_plan_${tier}_${days}`]);
    if (exact) return exact;
    const entry = object(tierRegistry()[variantId(item)])[String(tier)];
    if (entry && typeof entry === 'object') {
      const registered = positiveInt(entry[String(days)]);
      if (registered) return registered;
    }
    // Backward compatibility for carts created by the previous theme build.
    return positiveInt(properties[`_jefe_plan_${tier}`]) || positiveInt(entry);
  };
  const isTieredSubscription = (item) => {
    if (isGift(item) || !planId(item) || !bundleScope(item)) return false;
    const properties = object(item.properties);
    if (String(properties._jefe_tiered_subscription || '') === '1') return true;
    // Migration path for carts created with the immediately previous theme build:
    // those lines already carry _jefe_gift_scope but not the private plan ladder.
    return [1,2,3,4].some((tier) => [30,60].some((days) => Boolean(tierPlanFromItem(item, tier, days))));
  };

  async function reconcileSubscriptionTiers(initialCart) {
    let cart = initialCart || await getCart();
    let changed = false;

    // Each bundle flavor is a separate Shopify cart line. When the shopper edits the
    // cart, Shopify keeps the old selling_plan unless we explicitly replace it. Group
    // the subscription lines by bundle scope, calculate the new aggregate bag count,
    // then update each mismatched line with /cart/change.js.
    for (let pass = 0; pass < 20; pass += 1) {
      const groups = new Map();
      (cart.items || []).forEach((item, index) => {
        if (!isTieredSubscription(item)) return;
        const scope = bundleScope(item);
        const group = groups.get(scope) || { quantity: 0, entries: [] };
        group.quantity += Math.max(0, Number(item.quantity) || 0);
        group.entries.push({ item, index });
        groups.set(scope, group);
      });

      let mismatch = null;
      groups.forEach((group) => {
        if (mismatch || group.quantity <= 0) return;
        const tier = tierForQuantity(group.quantity);
        const storedDays = group.entries.map(({ item }) => requestedFrequencyDays(item)).find((days) => days === 30 || days === 60);
        // Preserve the cadence selected on the product page at every tier.
        // Older carts without a cadence property keep the previous tier defaults.
        const desiredDays = storedDays || (tier <= 2 ? 30 : 60);
        // Normally every line carries its own complete plan map. A group fallback also
        // repairs lines created during a previous theme version once any mapped line is
        // present in the same bundle.
        const fallbackPlan = group.entries.map(({ item }) => tierPlanFromItem(item, tier, desiredDays)).find(Boolean) || null;
        for (let i = group.entries.length - 1; i >= 0; i -= 1) {
          const entry = group.entries[i];
          const targetPlan = tierPlanFromItem(entry.item, tier, desiredDays) || fallbackPlan;
          const currentDays = requestedFrequencyDays(entry.item);
          const needsCadenceProperty = currentDays !== desiredDays;
          if (targetPlan && (String(targetPlan) !== planId(entry.item) || needsCadenceProperty)) {
            mismatch = { ...entry, targetPlan, tier, desiredDays, needsCadenceProperty, quantity: Number(entry.item.quantity) || 1 };
            break;
          }
        }
      });

      if (!mismatch) return { cart, changed };
      const payload = {
        line: mismatch.index + 1,
        quantity: mismatch.quantity,
        selling_plan: mismatch.targetPlan,
      };
      if (mismatch.needsCadenceProperty) {
        payload.properties = { ...object(mismatch.item.properties), _jefe_frequency_days: String(mismatch.desiredDays) };
      }
      cart = await post('change', payload);
      changed = true;
    }

    throw new Error('The subscription price could not be updated to the correct bag tier. Please refresh the cart before checkout.');
  }

  async function reconcileGifts(initialCart) {
    let cart = initialCart || await getCart();
    let changed = false;
    // A bounded re-read handles Shopify splitting lines or changing discounted keys.
    for (let pass = 0; pass < 3; pass += 1) {
      const expected = expectedGifts(cart);
      const current = managedGifts(cart);
      const updates = {};
      const missing = [];
      current.forEach((items,key) => items.forEach((item,index) => {
        const desired = expected.has(key) && index === 0 && !planId(item) ? 1 : 0;
        if (Number(item.quantity) !== desired) updates[item.key] = desired;
      }));
      expected.forEach((rule,key) => {
        if (!current.has(key) || current.get(key).every((item) => planId(item))) missing.push(giftItem(rule));
      });
      if (!Object.keys(updates).length && !missing.length) return { cart, changed };
      // update.js is used only to LOWER gift quantities/removals, never to add stock.
      if (Object.keys(updates).length) await post('update', { updates });
      if (missing.length) await post('add', { items: missing });
      changed = true;
      cart = await getCart();
    }
    throw new Error('The gift products could not be confirmed. Please check the cart before checkout.');
  }

  async function reconcile(initialCart) {
    const priced = await reconcileSubscriptionTiers(initialCart);
    const gifts = await reconcileGifts(priced.cart);
    return { cart: gifts.cart, changed: priced.changed || gifts.changed };
  }

  async function sectionsFor(ids, path) {
    ids = sectionIds(ids);
    if (!ids.length) return {};
    let sections = {};

    // Prefer bundled cart rendering. Unlike a product-page Section Rendering request,
    // /cart.js?sections=... remains reliable inside theme-editor previews and after a
    // multi-item add/reconcile. This mirrors Dawn's own Standard Actions refresh path.
    try {
      const cartUrl = new URL(endpoint('get'), location.origin);
      cartUrl.searchParams.set('sections', ids.join(','));
      const payload = await jsonRequest(cartUrl.pathname + cartUrl.search);
      sections = object(payload?.sections);
    } catch (_) {
      sections = {};
    }

    // Fallback to Shopify's Section Rendering API only for anything the cart endpoint
    // did not return. Keep the original page context for themes that depend on it.
    let missing = ids.filter((id) => typeof sections[id] !== 'string');
    if (missing.length) {
      try {
        const url = new URL(path || location.pathname + location.search, location.origin);
        if (url.origin !== location.origin) throw new Error('Invalid cart section URL.');
        url.searchParams.delete('section_id');
        url.searchParams.set('sections', missing.join(','));
        const rendered = await jsonRequest(url.pathname + url.search);
        sections = { ...sections, ...object(rendered) };
      } catch (_) { /* handled below */ }
    }

    missing = ids.filter((id) => typeof sections[id] !== 'string');
    if (missing.length) {
      // Last-resort single-section rendering. Dawn itself uses this endpoint when its
      // cart drawer receives a cart-update event, so it is a safe fallback when bundled
      // rendering is unavailable in a theme-editor or preview context.
      for (const id of [...missing]) {
        try {
          const sectionUrl = new URL(window.routes?.cart_url || `${root()}cart`, location.origin);
          sectionUrl.searchParams.set('section_id', id);
          const response = await fetch(sectionUrl.pathname + sectionUrl.search, {
            credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'text/html' },
          });
          const html = await response.text();
          if (response.ok && html && html.trim()) sections[id] = html;
        } catch (_) { /* handled below */ }
      }
    }

    missing = ids.filter((id) => typeof sections[id] !== 'string');
    if (missing.length) {
      const error = new Error('Your cart was updated, but its preview could not load. Open the cart to review it.');
      error.renderOnly = true;
      error.missingSections = missing;
      throw error;
    }
    return sections;
  }

  function message(text) {
    for (const id of ['CartDrawer-CartErrors','cart-errors']) {
      const node = document.getElementById(id);
      if (node) { node.textContent = text || ''; node.setAttribute('role','alert'); }
    }
  }
  function renderVisible(cart, sections) {
    const parser = new DOMParser();
    const drawer = document.querySelector('cart-drawer');
    if (drawer && sections['cart-drawer']) {
      const doc = parser.parseFromString(sections['cart-drawer'], 'text/html');
      const source = doc.querySelector('.drawer__inner');
      const target = drawer.querySelector('.drawer__inner');
      if (source && target) target.innerHTML = source.innerHTML;
      drawer.classList.toggle('is-empty', !cart.item_count);
    }
    for (const id of ['cart-icon-bubble','main-cart-items','main-cart-footer']) {
      const node = document.getElementById(id);
      const sectionId = id === 'cart-icon-bubble' ? id : node?.dataset.id;
      if (!node || !sections[sectionId]) continue;
      const doc = parser.parseFromString(sections[sectionId],'text/html');
      const selector = id === 'cart-icon-bubble' ? '.shopify-section' : '.js-contents';
      const source = doc.querySelector(selector);
      const target = id === 'cart-icon-bubble' ? node : node.querySelector(selector);
      if (source && target) target.innerHTML = source.innerHTML;
      if (id !== 'cart-icon-bubble') node.classList.toggle('is-empty', !cart.item_count);
    }
  }
  async function recover(error) {
    // Never replay an add after an uncertain response; Shopify may already have added it.
    try {
      const cart = await getCart();
      renderVisible(cart, await sectionsFor(visibleSectionIds()));
    } catch (_) { /* Keep the original error, not a secondary render error. */ }
    message(error.message);
  }

  function hasRules(formData) {
    return Boolean(formData.get('selling_plan')) && Array.from(formData.keys()).some((key) => /^properties\[_jefe_gift_\d+_variant\]$/.test(key));
  }
  function add(formData) {
    // Snapshot form values now; later pack clicks cannot mutate a queued request.
    const main = { id: positiveInt(formData.get('id')), quantity: positiveInt(formData.get('quantity')) || 1, properties: propertiesFromForm(formData) };
    if (formData.get('selling_plan')) main.selling_plan = positiveInt(formData.get('selling_plan'));
    const ids = sectionIds(formData.get('sections'));
    const path = formData.get('sections_url') || location.pathname + location.search;
    return enqueue(async () => {
      try {
        if (!main.id) throw new Error('Select an available product first.');
        const before = await getCart();
        const projected = { items: (before.items || []).map((item) => ({ ...item })) };
        const existing = projected.items.find((item) => sameMainLine(item,main));
        if (existing) existing.quantity += main.quantity;
        else projected.items.push(main);
        const expected = expectedGifts(projected);
        const current = managedGifts(before);
        const gifts = [];
        expected.forEach((rule,key) => { if (!current.has(key)) gifts.push(giftItem(rule)); });
        // One real Shopify multi-item request, not an unrelated background gift request.
        const response = await post('add', { items: [main,...gifts] });
        const result = await reconcile();
        const sections = await sectionsFor(ids,path);
        const mainResult = response.items?.find((item) => variantId(item) === String(main.id) && !isGift(item)) || response.items?.[0] || response;
        message('');
        return { ...mainResult, sections, _jefeGiftComplete: true };
      } catch (error) { await recover(error); throw error; }
    });
  }

  function addBundle(items, ids = [], path = location.pathname + location.search) {
    // Add all selected flavor variants together, plus any newly earned reward products,
    // in one Shopify request. The same private gift rules remain on every parent line so
    // cart quantity edits can be reconciled later.
    const snapshot = (Array.isArray(items) ? items : []).map((item) => ({
      ...item,
      id: positiveInt(item.id),
      quantity: positiveInt(item.quantity) || 1,
      properties: { ...object(item.properties) },
      ...(item.selling_plan ? { selling_plan: positiveInt(item.selling_plan) } : {}),
    }));
    return enqueue(async () => {
      try {
        if (!snapshot.length || snapshot.some((item) => !item.id)) throw new Error('Select at least one available flavor.');
        const before = await getCart();
        const projected = { items: (before.items || []).map((item) => ({ ...item })) };
        for (const main of snapshot) {
          const existing = projected.items.find((item) => sameMainLine(item, main));
          if (existing) existing.quantity = Number(existing.quantity || 0) + main.quantity;
          else projected.items.push(main);
        }
        const expected = expectedGifts(projected);
        const current = managedGifts(before);
        const gifts = [];
        expected.forEach((rule, key) => { if (!current.has(key)) gifts.push(giftItem(rule)); });
        const response = await post('add', { items: [...snapshot, ...gifts] });
        await reconcile();
        const sections = await sectionsFor(ids, path);
        const firstMain = response.items?.find((item) => !isGift(item)) || response.items?.[0] || response;
        message('');
        return { ...firstMain, items: response.items || [], sections, _jefeGiftComplete: true };
      } catch (error) { await recover(error); throw error; }
    });
  }

  function change(body) {
    const request = { ...body };
    return enqueue(async () => {
      try {
        const changedCart = await post('change', request);
        try {
          const result = await reconcile(changedCart);
          const completeSections = sectionIds(request.sections).every((id) => typeof changedCart.sections?.[id] === 'string');
          const sections = result.changed || !completeSections
            ? await sectionsFor(request.sections, request.sections_url)
            : changedCart.sections;
          message('');
          return { ...result.cart, sections, _jefeGiftComplete: true };
        } catch (error) {
          // The main quantity is already changed. Render its true state with a visible
          // gift error rather than telling the shopper their change did not happen.
          const cart = await getCart();
          const sections = await sectionsFor(request.sections, request.sections_url);
          return { ...cart, sections, _jefeGiftComplete: true, _jefeGiftError: error.message };
        }
      } catch (error) { await recover(error); throw error; }
    });
  }
  function clear(body = {}) { return enqueue(async () => ({ ...await post('clear',body), _jefeGiftComplete: true })); }
  function sync() {
    return enqueue(async () => {
      try {
        const result = await reconcile();
        if (result.changed) renderVisible(result.cart, await sectionsFor(visibleSectionIds()));
        message(''); return result;
      } catch (error) { await recover(error); throw error; }
    });
  }

  window.JefeGiftCart = { version: '4.0.0', hasRules, add, addBundle, change, clear, sync, get busy() { return pending > 0; } };

  // Other apps can still publish native cart updates. Our own awaited changes must
  // not launch another synchronization and overwrite a just-rendered drawer.
  function connect() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event?.cartData?._jefeGiftComplete) return Promise.resolve();
        return sync().catch(() => undefined);
      });
    }
    // Repair persisted carts with gift rules on full page reload, not empty previews.
    if (!window.Shopify?.designMode && document.querySelector('[data-jefe-managed-gift], [data-jefe-gift-parent]')) sync().catch(() => undefined);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', connect, { once: true });
  else connect();

  // Capture standard checkout form submissions (including keyboard submission).
  // Look up the new form/button after rendering; the original may be detached.
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    const submitter = event.submitter;
    if (!(form instanceof HTMLFormElement) || submitter?.name !== 'checkout' || replaying.has(form)) return;
    event.preventDefault(); event.stopImmediatePropagation();
    const formId = form.id;
    const submitterId = submitter.id;
    const previousDisabled = submitter.disabled;
    submitter.disabled = true;
    try {
      // Let the native debounced quantity change join the mutation queue first.
      const uncommitted = Array.from(document.querySelectorAll('cart-items input.quantity__input, cart-drawer-items input.quantity__input')).find((input) => !input.disabled && input.value !== input.getAttribute('value'));
      if (uncommitted) {
        uncommitted.dispatchEvent(new Event('change', { bubbles: true }));
      }
      await new Promise((resolve) => setTimeout(resolve, 350));
      await sync();
      const currentForm = document.getElementById(formId) || (form.isConnected ? form : null);
      const currentButton = document.getElementById(submitterId) || (submitter.isConnected ? submitter : null);
      if (!currentForm || !currentButton) throw new Error('Please reload your cart before checking out.');
      currentButton.disabled = false;
      replaying.add(currentForm);
      try { currentForm.requestSubmit(currentButton); }
      finally { replaying.delete(currentForm); }
    } catch (error) { message(error.message); }
    finally { if (submitter.isConnected) submitter.disabled = previousDisabled; }
  }, true);
})();
